// CU-86d45b2z1 — the read-only customer Product Catalog over commerce's customer-safe catalogue endpoint.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Input, Pagination, Result, Row, Select, Space } from "antd";
import { useQuery } from "@tanstack/react-query";

import CustomerLayout from "../../components/customer/CustomerLayout";
import { EmptyState, InlineLoading, PageContainer, SectionCard } from "../../components/shared";
import { classifyCustomerCatalogError, fetchCustomerCatalog } from "../../api/customer/customerCatalogApi";
import CustomerProductCard from "./CustomerProductCard";

export const CATALOG_PAGE_SIZE = 24;

const ProductCatalogPage: React.FC = () => {
	const navigate = useNavigate();
	const [page, setPage] = useState(1);
	const [searchDraft, setSearchDraft] = useState("");
	const [search, setSearch] = useState("");
	const [categoryId, setCategoryId] = useState<number | undefined>(undefined);

	const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
		queryKey: ["customer-catalog", page, search, categoryId],
		queryFn: () => fetchCustomerCatalog({ page, perPage: CATALOG_PAGE_SIZE, search, categoryId }),
		// CU-86d45b2z1: no automatic retry; a 403 is a decision and each outcome renders its own action.
		retry: false,
	});

	const outcome = isError ? classifyCustomerCatalogError(error) : null;

	const submitSearch = (value: string) => {
		setSearch(value.trim());
		setPage(1);
	};

	const changeCategory = (value: number | undefined) => {
		setCategoryId(value);
		setPage(1);
	};

	const renderBody = () => {
		if (isLoading) return <InlineLoading message="Loading products…" minHeight={300} />;

		if (outcome === "session_refresh_required") {
			return (
				<div data-testid="catalog-refresh-required">
					<Result
						status="warning"
						title="Please sign in again"
						subTitle="Your session was started before customer access was enabled, or it has expired. Sign in again to browse the catalogue."
						extra={
							<Button type="primary" onClick={() => navigate("/logout")} data-testid="catalog-sign-in-again">
								Sign in again
							</Button>
						}
					/>
				</div>
			);
		}

		if (outcome === "forbidden") {
			return (
				<div data-testid="catalog-forbidden">
					<Result
						status="403"
						title="Customer access is not available for this account"
						subTitle="This account is not part of the customer directory. If you believe it should be, please contact the clinic."
					/>
				</div>
			);
		}

		if (outcome === "unavailable") {
			return (
				<div data-testid="catalog-error">
					<Result
						status="error"
						title="We couldn't load the catalogue"
						subTitle="Something went wrong on our side. Please try again."
						extra={
							<Button onClick={() => refetch()} loading={isFetching} data-testid="catalog-retry">
								Try again
							</Button>
						}
					/>
				</div>
			);
		}

		if (!data || data.data.length === 0) {
			return (
				<EmptyState
					title="No products found"
					description={search || categoryId ? "Try a different search or category." : "There are no products to show right now."}
				/>
			);
		}

		return (
			<>
				<Row gutter={[16, 16]} data-testid="catalog-grid">
					{data.data.map((product) => (
						<Col key={product.product_id} xs={24} sm={12} md={8} lg={6}>
							<CustomerProductCard product={product} />
						</Col>
					))}
				</Row>
				{data.pagination.total_pages > 1 && (
					<div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
						<Pagination
							current={data.pagination.current_page}
							pageSize={data.pagination.per_page}
							total={data.pagination.total_items}
							showSizeChanger={false}
							onChange={(next) => setPage(next)}
						/>
					</div>
				)}
			</>
		);
	};

	return (
		<CustomerLayout>
			<PageContainer title="Products" subtitle="Browse the products available from your clinic" maxWidth={1280}>
				<SectionCard>
					<Space wrap size={12}>
						<Input.Search
							allowClear
							placeholder="Search products…"
							value={searchDraft}
							onChange={(event) => setSearchDraft(event.target.value)}
							onSearch={submitSearch}
							style={{ width: 280 }}
							data-testid="catalog-search"
						/>
						<Select
							allowClear
							placeholder="All categories"
							value={categoryId}
							onChange={changeCategory}
							style={{ width: 220 }}
							options={(data?.categories ?? []).map((category) => ({ value: category.id, label: category.name }))}
							data-testid="catalog-category"
						/>
					</Space>
				</SectionCard>

				{renderBody()}
			</PageContainer>
		</CustomerLayout>
	);
};

export default ProductCatalogPage;
